import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/utils/catalog";

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname === "/search") {
      const params = new URLSearchParams(location.search);
      setKeyword(params.get("q") ?? "");
    }
  }, [location.pathname, location.search]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const navLinkClass = (active: boolean) =>
    `px-3 py-2 text-sm whitespace-nowrap transition-colors ${
      active
        ? "text-primary-600 font-medium"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-3 md:gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-play-fill text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-base font-semibold tracking-tight text-foreground-950 whitespace-nowrap">
                黄色片免费看
              </span>
              <span className="mt-1 hidden text-[10px] tracking-[0.18em] text-foreground-500 sm:block">
                FREE VIDEO LIBRARY
              </span>
            </span>
          </Link>

          <nav className="hidden items-center lg:flex">
            <Link to="/" className={navLinkClass(isActive("/"))}>
              首页
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCategoryOpen((prev) => !prev)}
                className={`${navLinkClass(
                  isActive("/category"),
                )} flex cursor-pointer items-center gap-1`}
              >
                分类
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    categoryOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full w-56 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5">
                  {categories.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/category/${item.slug}`}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                    >
                      <i className={`${item.icon} text-base text-primary-500`} />
                      {item.name}
                      <span className="ml-auto text-[11px] text-foreground-400">
                        {item.tagline}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/search" className={navLinkClass(isActive("/search"))}>
              搜索
            </Link>
            <Link to="/about" className={navLinkClass(isActive("/about"))}>
              关于
            </Link>
          </nav>

          <form
            onSubmit={submitSearch}
            className="hidden items-center md:flex md:w-[300px] lg:w-[320px]"
          >
            <div className="flex w-full items-center gap-1 rounded-full border border-background-300 bg-background-100 py-1 pl-3.5 pr-1">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片标题、导演或标签"
                aria-label="搜索影片"
                className="min-w-0 flex-1 bg-transparent py-1.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="flex h-8 items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-3.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </div>
          </form>

          <button
            type="button"
            aria-label="打开导航菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-background-300 text-foreground-700 lg:hidden"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-3-line"} text-xl`} />
          </button>
        </div>

        <form onSubmit={submitSearch} className="flex py-2.5 md:hidden">
          <div className="flex w-full items-center gap-1 rounded-full border border-background-300 bg-background-100 py-1 pl-3.5 pr-1">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片标题、导演或标签"
              aria-label="搜索影片"
              className="min-w-0 flex-1 bg-transparent py-1.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="flex h-8 items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-3.5 text-xs font-medium text-background-50"
            >
              搜索
            </button>
          </div>
        </form>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="flex flex-col px-4 py-3">
            <Link
              to="/"
              className="flex items-center gap-2 py-3 text-sm text-foreground-800"
            >
              <i className="ri-home-4-line text-base text-primary-500" /> 首页
            </Link>
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className="flex items-center gap-2 border-t border-background-200/70 py-3 text-sm text-foreground-800"
              >
                <i className={`${item.icon} text-base text-primary-500`} /> {item.name}
              </Link>
            ))}
            <Link
              to="/search"
              className="flex items-center gap-2 border-t border-background-200/70 py-3 text-sm text-foreground-800"
            >
              <i className="ri-search-line text-base text-primary-500" /> 搜索
            </Link>
            <Link
              to="/about"
              className="flex items-center gap-2 border-t border-background-200/70 py-3 text-sm text-foreground-800"
            >
              <i className="ri-information-line text-base text-primary-500" /> 关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}